import { apiClient } from './apiClient'
import {
  RankingSeriesDTO,
  CreateRankingSeriesRequest,
  RankingEventDTO,
  CreateRankingEventRequest,
  RankingStandingDTO,
  PointSystemDTO
} from '../types/dtos'

/**
 * Service for managing Order of Merit rankings
 */
class RankingService {
  /**
   * Get all ranking series for current tenant
   */
  async getRankingSeries(): Promise<RankingSeriesDTO[]> {
    const response = await apiClient.get<RankingSeriesDTO[]>('/ranking/series')
    return response.data
  }

  /**
   * Get a specific ranking series by ID
   */
  async getRankingSeriesById(id: number): Promise<RankingSeriesDTO> {
    const response = await apiClient.get<RankingSeriesDTO>(`/ranking/series/${id}`)
    return response.data
  }

  /**
   * Create a new ranking series
   */
  async createRankingSeries(request: CreateRankingSeriesRequest): Promise<RankingSeriesDTO> {
    const response = await apiClient.post<RankingSeriesDTO>('/ranking/series', request)
    return response.data
  }

  /**
   * Update an existing ranking series
   */
  async updateRankingSeries(id: number, request: CreateRankingSeriesRequest): Promise<RankingSeriesDTO> {
    const response = await apiClient.put<RankingSeriesDTO>(`/ranking/series/${id}`, request)
    return response.data
  }

  /**
   * Delete a ranking series
   */
  async deleteRankingSeries(id: number): Promise<void> {
    await apiClient.delete(`/ranking/series/${id}`)
  }

  /**
   * Get all ranking events (series linked to rankings)
   */
  async getRankingEvents(rankingSeriesId: number): Promise<RankingEventDTO[]> {
    const response = await apiClient.get<RankingEventDTO[]>(`/ranking/events/series/${rankingSeriesId}`)
    return response.data
  }

  /**
   * Create a new ranking event (link series to ranking)
   */
  async createRankingEvent(request: CreateRankingEventRequest): Promise<RankingEventDTO> {
    const response = await apiClient.post<RankingEventDTO>('/ranking/events', request)
    return response.data
  }

  /**
   * Publish points for a ranking event
   */
  async publishPoints(eventId: number): Promise<void> {
    await apiClient.post(`/ranking/events/${eventId}/publish`)
  }

  /**
   * Unpublish points for corrections
   */
  async unpublishPoints(eventId: number): Promise<void> {
    await apiClient.post(`/ranking/events/${eventId}/unpublish`)
  }

  /**
   * Delete a ranking event
   */
  async deleteRankingEvent(eventId: number): Promise<void> {
    await apiClient.delete(`/ranking/events/${eventId}`)
  }

  /**
   * Get leaderboard standings for a ranking series
   */
  async getStandings(rankingSeriesId: number): Promise<RankingStandingDTO[]> {
    const response = await apiClient.get<RankingStandingDTO[]>(
      `/ranking/standings/series/${rankingSeriesId}`
    )
    return response.data
  }

  /**
   * Get player's standing in a ranking series
   */
  async getPlayerStanding(rankingSeriesId: number, userId: number): Promise<RankingStandingDTO> {
    const response = await apiClient.get<RankingStandingDTO>(
      `/ranking/standings/series/${rankingSeriesId}/user/${userId}`
    )
    return response.data
  }

  /**
   * Get all available point systems
   */
  async getPointSystems(): Promise<PointSystemDTO[]> {
    const response = await apiClient.get<PointSystemDTO[]>('/ranking/point-systems')
    return response.data
  }
}

export const rankingService = new RankingService()
